import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getProjects } from '../../api/pmsApi';

function ProjectList({ status = 'in_progress' }) {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 페이징 관련 상태
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  // 검색 관련 상태 (완료 목록일 때만 사용)
  const [searchType, setSearchType] = useState('project_name');
  const [searchValue, setSearchValue] = useState('');

  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({});

  const statusMeta = {
    pending: { title: '대기중 프로젝트', icon: 'fa-hourglass-half', borderClass: 'border-warning', textClass: 'text-warning', badgeLabel: '대기중', badgeClass: 'bg-warning text-dark' },
    ready: { title: '시작 전 프로젝트', icon: 'fa-pause-circle', borderClass: 'border-warning', textClass: 'text-warning', badgeLabel: '시작 전', badgeClass: 'bg-warning text-dark' },
    in_progress: { title: '진행중 프로젝트', icon: 'fa-play-circle', borderClass: 'border-primary', textClass: 'text-primary', badgeLabel: '진행중', badgeClass: 'bg-primary' },
    review: { title: '검토 대기중 프로젝트', icon: 'fa-clipboard-check', borderClass: 'border-info', textClass: 'text-info', badgeLabel: '검토대기', badgeClass: 'bg-info text-dark' },
    completed: { title: '완료된 프로젝트', icon: 'fa-check-circle', borderClass: 'border-success', textClass: 'text-success', badgeLabel: '완료됨', badgeClass: 'bg-success' },
    canceled: { title: '취소/삭제된 프로젝트', icon: 'fa-trash-alt', borderClass: 'border-danger', textClass: 'text-danger', badgeLabel: '취소됨', badgeClass: 'bg-danger' },
    rejected: { title: '반려된 프로젝트', icon: 'fa-ban', borderClass: 'border-danger', textClass: 'text-danger', badgeLabel: '반려됨', badgeClass: 'bg-danger text-white' }
  };

  const meta = statusMeta[status] || statusMeta['in_progress'];

  const fetchProjects = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = isSearching ? { status, ...searchParams } : { status };
      const res = await getProjects(params, token);
      if (res && res.ok) {
        setList(res.data || []);
        setTotal(res.data?.length || 0);
        setPage(1);
      } else {
        setList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('프로젝트 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [status, token, isSearching, searchParams]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // 숫자를 통화(원) 형식으로 변환
  const formatCurrency = (val) => {
    if (!val) return '0원';
    return Number(val).toLocaleString('ko-KR') + '원';
  };

  // 검색 로직
  const handleSearch = () => {
    if (!searchValue || searchValue.trim() === '') {
      alert("검색어가 지정되지 않았습니다.");
      return;
    }

    const params = {};
    if (searchType === 'project_name') params.project_name = searchValue;
    if (searchType === 'author') params.author = searchValue;
    if (searchType === 'leader') params.leader = searchValue;
    if (searchType === 'member') params.member = searchValue;

    setSearchParams(params);
    setIsSearching(true);
  };

  const handleReset = () => {
    setIsSearching(false);
    setSearchType('project_name');
    setSearchValue('');
    setSearchParams({});
  };

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchValue('');
  };

  return (
    <div className="container-fluid p-4">
      {/* 🚀 상단 헤더 & 타이틀 */}
      {/* 🚀 상단 헤더 & 타이틀 */}
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded-3 shadow-sm border border-light">
        <h4 className="mb-0 fw-bold text-dark d-flex align-items-center">
          <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
            <i className={`fas ${meta.icon} fs-5`}></i>
          </div>
          {meta.title}

          {isSearching ? (
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 ms-3 rounded-pill fw-semibold shadow-sm px-2 py-1" style={{ fontSize: '0.55em', letterSpacing: '-0.5px', verticalAlign: 'middle' }}>
              <i className="fas fa-search me-1"></i> 검색 결과: 총 {total}건
            </span>
          ) : (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 ms-3 rounded-pill fw-semibold shadow-sm px-2 py-1" style={{ fontSize: '0.55em', letterSpacing: '-0.5px', verticalAlign: 'middle' }}>
              <i className="fas fa-list me-1"></i> 총 {total}건
            </span>
          )}
        </h4>
        <div className="d-flex gap-2">
          <style>{`
            .refresh-btn:hover .fa-sync-alt { transform: rotate(180deg); }
            .refresh-btn .fa-sync-alt { transition: transform 0.3s ease-in-out; }
            .header-btn { transition: all 0.2s ease-in-out; }
            .header-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 6px rgba(0,0,0,0.08) !important; }
          `}</style>
          <button className="btn btn-primary fw-bold btn-sm header-btn shadow-sm" onClick={() => navigate('/pms/projects/new')}>
            <i className="fas fa-plus me-1"></i>프로젝트 등록
          </button>
          <button className="btn btn-light btn-sm d-flex align-items-center fw-bold text-dark refresh-btn border header-btn shadow-sm" onClick={fetchProjects}>
            <i className="fas fa-sync-alt me-1 text-primary"></i> 새로고침
          </button>
        </div>
      </div>

      {/* 🔍 검색 영역 */}
      <div className="card mb-0 border-0 shadow-sm rounded-4 rounded-bottom-0">
        <div className="card-body row gx-2 align-items-center m-0" style={{ paddingTop: "15px", paddingBottom: "0px" }}>
          <div className="col-auto">
            <select className="form-select" value={searchType} onChange={handleSearchTypeChange}>
              <option value="project_name">프로젝트명</option>
              <option value="author">요청자</option>
              <option value="leader">책임자(PM)</option>
              <option value="member">담당자</option>
            </select>
          </div>

          <div className="col-md-2">
            <input
              type="text"
              className="form-control"
              placeholder="검색어 입력"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
          </div>

          <div className="col-auto">
            <button className="btn btn-secondary" onClick={handleSearch} style={{ backgroundColor: '#6c8496', border: 'none' }}>
              <i className="fas fa-search me-1"></i>검색
            </button>
          </div>
          <div className="col-auto">
            <button className="btn btn-outline-secondary" onClick={handleReset}>초기화</button>
          </div>
        </div>
      </div>

      {/* 📋 데이터 리스트 영역 */}
      <div className="card border-0 shadow-sm rounded-4 rounded-top-0 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0" style={{ minWidth: '1050px', tableLayout: 'fixed' }}>
            <thead className="table-light">
              <tr style={{ height: '40px' }}>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '90px', borderBottom: '2px solid #ced4da' }}>상태</th>
                <th className="text-dark fw-bold align-middle" style={{ width: 'auto', borderBottom: '2px solid #ced4da' }}>프로젝트 명</th>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '220px', borderBottom: '2px solid #ced4da' }}>검토자(요청자)</th>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '180px', borderBottom: '2px solid #ced4da' }}>책임자(PM)</th>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '140px', borderBottom: '2px solid #ced4da' }}>진척도(%)</th>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '150px', borderBottom: '2px solid #ced4da' }}>배정 예산</th>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '200px', borderBottom: '2px solid #ced4da' }}>기간</th>
                <th className="text-center text-dark fw-bold align-middle" style={{ width: '110px', borderBottom: '2px solid #ced4da' }}>작성일</th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    <div className="spinner-border text-secondary me-2" role="status" size="sm"></div>
                    데이터를 불러오는 중입니다...
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    <i className="fas fa-box-open mb-3 fs-2 text-light"></i>
                    <p className="mb-0 fs-6 text-secondary">현재 해당하는 프로젝트가 없습니다.</p>
                  </td>
                </tr>
              ) : (
                list.slice((page - 1) * limit, page * limit).map(item => {
                  let badgeLabel = status === 'canceled' ? (item.is_deleted ? '삭제됨' : '취소됨') : (statusMeta[item.status]?.badgeLabel || meta.badgeLabel);
                  let badgeClass = status === 'canceled' ? (item.is_deleted ? 'bg-danger text-white' : 'bg-warning text-dark') : (statusMeta[item.status]?.badgeClass || meta.badgeClass);

                  const isCompleted = item.status === 'completed' || item.status === 'canceled' || item.status === 'rejected';
                  const todayStr = new Date().toISOString().split('T')[0];
                  const isDelayed = !isCompleted && item.end_date && item.end_date < todayStr;

                  if (isDelayed) {
                    badgeLabel = (item.status === 'ready' || item.status === 'pending') ? '시작 지연' : '지연';
                    badgeClass = 'bg-danger text-white';
                  }
                  let progressClass = item.progress_rate === 100 ? 'bg-success' : 'bg-primary progress-bar-striped progress-bar-animated';
                  let textClass = item.progress_rate === 100 ? 'text-success' : 'text-primary';

                  return (
                    <tr key={item.id} onClick={() => navigate(`/pms/projects/${item.id}`)} style={{ cursor: 'pointer', transition: 'all 0.2s ease-in-out' }}>

                      {/* 상태 뱃지 */}
                      <td className="text-center">
                        <span className={`badge rounded-pill px-3 py-2 ${badgeClass}`} style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                          {badgeLabel}
                        </span>
                      </td>

                      {/* 제목 */}
                      <td className="fw-bold text-dark fs-6">
                        <div className="d-flex align-items-center gap-2">
                          {item.instruction_id && (
                            <span className="badge bg-dark text-white rounded-pill px-2" style={{ fontSize: '0.65rem' }}>
                              요청
                            </span>
                          )}
                          <div className="text-truncate" style={{ minWidth: 0 }} title={item.title}>
                            {item.title}
                          </div>
                        </div>
                      </td>

                      {/* 검토자 */}
                      <td className="text-center text-primary fw-bold">
                        {(() => {
                          if (!item.reviewer) return <span className="text-muted">미지정</span>;
                          const reviewerName = item.reviewer.name;
                          const reviewerPos = item.reviewer.org?.position || '';
                          const reviewerDept = item.reviewer.org?.dept_name || '';
                          const display = reviewerPos
                            ? `${reviewerName} / ${reviewerPos}${reviewerDept ? '(' + reviewerDept + ')' : ''}`
                            : reviewerName;
                          return (
                            <div className="badge bg-light text-dark border px-2 py-1 text-truncate" style={{ fontSize: '0.75rem', maxWidth: '100%' }} title={display}>
                              <i className="fas fa-user-check me-1 text-secondary"></i>{display}
                            </div>
                          );
                        })()}
                      </td>

                      {/* 책임자(PM) */}
                      <td className="text-center text-primary fw-bold">
                        {(() => {
                          if (!item.leader) return <span className="text-muted">미지정</span>;
                          const leaderName = item.leader.name;
                          const leaderPos = item.leader.org?.position || '';
                          const leaderDept = item.leader.org?.dept_name || '';
                          const display = leaderPos
                            ? `${leaderName} / ${leaderPos}${leaderDept ? '(' + leaderDept + ')' : ''}`
                            : leaderName;
                          return (
                            <div className="badge bg-light text-dark border px-2 py-1 text-truncate" style={{ fontSize: '0.75rem', maxWidth: '100%' }} title={display}>
                              <i className="fas fa-user me-1 text-secondary"></i>{display}
                            </div>
                          );
                        })()}
                      </td>

                      {/* 진척도 프로그레스 바 */}
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center">
                          <span className={`me-2 fw-bold text-end ${textClass}`} style={{ width: '45px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{item.progress_rate}%</span>
                          <div className="progress flex-grow-1" style={{ height: '6px', maxWidth: '80px' }}>
                            <div
                              className={`progress-bar ${progressClass}`}
                              role="progressbar"
                              style={{ width: `${item.progress_rate}%` }}
                              aria-valuenow={item.progress_rate} aria-valuemin="0" aria-valuemax="100">
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* 예산 */}
                      <td className="text-end text-secondary fw-bold pe-4">
                        {formatCurrency(item.budget)}
                      </td>

                      {/* 기간 */}
                      <td className="text-center fw-bold" style={{ fontSize: '0.85rem' }}>
                        <span className="text-muted">{item.start_date ? item.start_date.substring(0, 10) : '-'}</span>
                        <span className="mx-1">~</span>
                        <span className="text-danger">{item.end_date ? item.end_date.substring(0, 10) : '-'}</span>
                      </td>

                      {/* 작성일 */}
                      <td className="text-center text-muted fw-bold" style={{ fontSize: '0.85rem' }}>
                        {item.created_at ? (() => {
                          const d = new Date(item.created_at);
                          if (isNaN(d.getTime())) return item.created_at.substring(0, 10);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        })() : '-'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 📄 페이지네이션 (InstructionList와 동일한 인라인 코드) */}
      {(() => {
        const totalPages = Math.max(1, Math.ceil(total / limit));
        const maxVisible = 5;
        let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
        const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

        return (
          <div className="position-relative d-flex justify-content-center align-items-center mt-3 p-2">
            <div className="position-absolute" style={{ left: '10px' }}>
              <span className="badge rounded text-white" style={{ backgroundColor: '#777b8c', fontSize: '13px', padding: '0.25rem 0.6rem' }}>
                {page} / {totalPages} (총 : {total})
              </span>
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link text-secondary" onClick={() => setPage(p => Math.max(1, p - 1))}>« 이전</button>
                </li>
                {total > 0 && pages.map(p => (
                  <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                  </li>
                ))}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link text-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>다음 »</button>
                </li>
              </ul>
            </nav>
          </div>
        );
      })()}

    </div>
  );
}

export default ProjectList;
